import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "app/store";
import { getEvaluationFormsByContextIds } from "features/agentScoringRules/evaluationFormLookup";

/**
 * Redux cache of evaluation-form display names keyed by form `contextId`.
 *
 * Used by the agent-scoring-rules **list** (`AgentScoringRulesTable`) to turn a
 * rule's `evaluationFormContextId` into the "Rule Form" column label. Filled by
 * `fetchAgentScoringRules` via `resolveEvaluationFormNames`. The rule editor does
 * not read this slice — it loads dropdown options through `searchEvaluationForms`
 * and resolves a missing selected form via `getEvaluationFormsByContextIds`
 * directly.
 *
 * The evaluation forms editor (`qualityFormSlice`) has its own `loadFormNames`
 * (all forms, incl. surveys, for a name-uniqueness check) and is intentionally
 * left as-is; it could migrate onto this cache later.
 */
type EvaluationFormNamesState = {
    /** Resolved form names keyed by contextId. */
    names: Record<string, string>;
};

const initialState: EvaluationFormNamesState = {
    names: {},
};

/**
 * Resolves display names for the given form contextIds via the bulk-by-context
 * endpoint and merges them into the cache. Best-effort: on rejection the reducer
 * leaves previously resolved names untouched so consumers keep rendering.
 */
export const resolveEvaluationFormNames = createAsyncThunk(
    "evaluationFormNames/resolveEvaluationFormNames",
    async (contextIds: string[]) => {
        const forms = await getEvaluationFormsByContextIds(contextIds);
        return forms.reduce<Record<string, string>>((acc, form) => {
            acc[form.contextId] = form.name;
            return acc;
        }, {});
    }
);

const evaluationFormNamesSlice = createSlice({
    name: "evaluationFormNames",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(
            resolveEvaluationFormNames.fulfilled,
            (state, { payload }) => {
                // Merge rather than replace: the cache accumulates names keyed by
                // contextId, so a lookup for a smaller id set never drops names
                // resolved by an earlier one. Stale entries are harmless — they
                // are only read when a rule references that contextId.
                state.names = { ...state.names, ...payload };
            }
        );
        // `rejected` is intentionally unhandled: a failed best-effort lookup
        // keeps the previously resolved names in place.
    },
});

export const selectEvaluationFormNames = (state: RootState) =>
    state.evaluationFormNames.names;

export const selectEvaluationFormName = (state: RootState, contextId: string) =>
    state.evaluationFormNames.names[contextId];

export default evaluationFormNamesSlice.reducer;
